// src/Confetti.js
import React, { useState, useEffect, lazy, Suspense } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";

// react-confetti is a relatively heavy dependency that is only needed for a
// brief celebratory animation, so load it on demand rather than in the shell.
const ConfettiComponent = lazy(() => import("react-confetti"));

const Confetti = ({ trigger, isMobile = false }) => {
  const { t } = useTranslation();
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [showConfetti, setShowConfetti] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [isFading, setIsFading] = useState(false);

  useEffect(() => {
    const updateWindowSize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    // Set initial size
    updateWindowSize();

    // Update on resize
    window.addEventListener("resize", updateWindowSize);
    return () => window.removeEventListener("resize", updateWindowSize);
  }, []);

  useEffect(() => {
    if (trigger) {
      setShowConfetti(true);
      setIsFading(false);
      if (isMobile) setShowBanner(true);
      const fadeTimer = setTimeout(() => setIsFading(true), 3200);
      const hideTimer = setTimeout(() => {
        setShowConfetti(false);
        setShowBanner(false);
      }, 4500);
      return () => {
        clearTimeout(fadeTimer);
        clearTimeout(hideTimer);
      };
    }
  }, [trigger, isMobile]);

  if (!showConfetti) return null;

  // Use Portal to render directly into body, bypassing any parent transforms
  return ReactDOM.createPortal(
    <div
      className={`confetti-wrap ${isFading ? "confetti-fade-out" : ""}`}
      style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", zIndex: 9999, pointerEvents: "none" }}
    >
      {isMobile && showBanner && (
        <div className="level-up-banner" role="status" aria-live="polite">
          {t("levelUp")}
        </div>
      )}
      <Suspense fallback={null}>
        <ConfettiComponent
          width={windowSize.width}
          height={windowSize.height}
          recycle={false}
          numberOfPieces={1000}
          gravity={0.3}
          initialVelocityY={20}
          confettiSource={{ 
            x: 0, 
            y: 0, 
            w: windowSize.width, 
            h: 0 
          }}
          colors={["#3677ef", "#4e32e0", "#2daa44", "#f9b31c", "#399cfa", "#5b8af5", "#fcc520", "#1d9f45"]}
        />
      </Suspense>
    </div>,
    document.body
  );
};

export default Confetti;
